/**
 * Serialise one kind of state-changing call (a Data Mapper screen's connect /
 * disconnect) so they never overlap, and let the LATEST intent win.
 *
 * A call waits for the one in flight to settle. Several asked meanwhile
 * collapse: when the in-flight one settles, only the most recent waiting call
 * runs and every older waiting one is skipped (it never reaches the wire). The
 * in-flight call is never interrupted; its answer still lands, then the latest
 * intent runs on top of it. So Connect → Disconnect → Connect, clicked while
 * the first is still dialing, ends connected after exactly two calls.
 */
export type IntentOutcome<T> = { ran: true; value: T } | { ran: false };

export class LatestIntentQueue {
  private tail: Promise<unknown> = Promise.resolve();
  private latest = 0;
  /** Calls asked and not yet settled (in flight or waiting). */
  private unsettled = 0;

  /**
   * Run `op` once every earlier call has settled, unless a newer call is asked
   * first. With nothing in flight it starts at once (synchronously up to its
   * first await), so a store's `busy` flag is set before this returns.
   */
  run<T>(op: () => Promise<T>): Promise<IntentOutcome<T>> {
    const mine = ++this.latest;
    const start = async (): Promise<IntentOutcome<T>> =>
      mine === this.latest ? { ran: true, value: await op() } : { ran: false };
    const outcome = this.unsettled === 0 ? start() : this.tail.then(start);
    this.unsettled++;
    this.tail = outcome
      .catch(() => undefined)
      .then(() => {
        this.unsettled--;
      });
    return outcome;
  }

  /** Skip every call still waiting (the route was left, the profile changed). The in-flight one finishes. */
  supersede(): void {
    this.latest++;
  }
}
